import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { Fragment, useMemo, useState } from "react";
import { Alert, IconButton, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Cell, Column, HeaderGroup, Row, useExpanded, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { TFunction } from "i18next";
import { GlobalFilter, renderFilterTypes } from "utils/react-table";
import { DeleteTwoTone, DisconnectOutlined, EditTwoTone, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { EmptyTable, HeaderSort, TablePagination } from "components/third-party/ReactTable";
import MainCard from "components/MainCard";
import ScrollX from "components/ScrollX";
import { IExternalDevice } from "models/IExternalDevice";
import AddExternalDeviceDialog from "components/dialogs/ExternalDevices/AddExternalDeviceDialog";
import UpdateExternalDeviceDialog from "components/dialogs/ExternalDevices/UpdateExternalDeviceDialog";
import React from "react";
import { UserRole } from "models/IUser";
import GenericDeleteDialog from "components/dialogs/Generic/GenericDeleteDialog";
import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/authSlice";
import { useGetExternalDevicesQuery, useGetUsersQuery, useGetOrganizationsQuery, usePostExternalDeviceMutation, usePatchExternalDeviceMutation, useDeleteExternalDeviceMutation, useGetBlobSASTokenMutation } from "features/api/apiSlice";
import { useTranslation } from "react-i18next";
import { getDeviceBrandName } from "helpers/ExternalDeviceHelper";
import { DeviceBrandId, DeviceBrandName } from "models/ISensor";
import UploadExternalDeviceDataDialog from "components/dialogs/ExternalDevices/UploadExternalDeviceDataDialog";
import { BlockBlobClient } from "@azure/storage-blob";
import moment from "moment";
import i18n from "app/i18n";

// ==============================|| DEVICES PAGE ||============================== //

export default function DevicesPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const authUser = useAppSelector(selectCurrentUser);

  const { data: externalDevices, isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetExternalDevicesQuery({})
  const { data: users, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authUser!!.id!!, role: UserRole.Patient })
  const { data: organizations, isLoading: isLoadingOrganizations, isFetching: isFetchingOrganizations } = useGetOrganizationsQuery({})

  const [externalDevice, setExternalDevice] = useState<IExternalDevice | null>(null);
  const [showAddExternalDeviceDialog, setShowAddExternalDeviceDialog] = useState<boolean>(false);
  const [showUpdateExternalDeviceDialog, setShowUpdateExternalDeviceDialog] = useState<boolean>(false);
  const [showDeleteExternalDeviceDialog, setShowDeleteExternalDeviceDialog] = useState<boolean>(false);
  const [deleteDialogTitle, setDeleteDialogTitle] = useState<string>('');
  const [showUploadExternalDeviceDataDialog, setShowUploadExternalDeviceDataDialog] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const handleOpenAddExternalDeviceDialog = () => {
    setShowAddExternalDeviceDialog(true)
  };

  const handleCloseAddExternalDeviceDialog = () => {
    setShowAddExternalDeviceDialog(false);
  };

  const handleOpenUpdateExternalDeviceDialog = (externalDevice: IExternalDevice) => {
    setExternalDevice(externalDevice)
    setShowUpdateExternalDeviceDialog(true)
  };

  const handleCloseUpdateExternalDeviceDialog = () => {
    setShowUpdateExternalDeviceDialog(false);
    setExternalDevice(null);
  };

  const handleOpenDeleteExternalDeviceDialog = (externalDevice: IExternalDevice) => {
    const selectedExternalDeviceId = (externalDevice.id ?? '') as string;
    const titleTemplate = externalDevice.isConnection
      ? (t('do_you_want_to_disconnect_x', 'Do you want to disconnect {0}?') as string)
      : (t('do_you_want_to_delete_x', 'Do you want to delete {0}?') as string);

    setDeleteDialogTitle(titleTemplate.replace('{0}', selectedExternalDeviceId));
    setExternalDevice(externalDevice)
    setShowDeleteExternalDeviceDialog(true)
  };

  const handleCloseDeleteExternalDeviceDialog = () => {
    setShowDeleteExternalDeviceDialog(false);
    setDeleteDialogTitle('');
    setExternalDevice(null);
  };

  const handleOpenUploadExternalDeviceDataDialog = (externalDevice: IExternalDevice) => {
    setExternalDevice(externalDevice)
    setShowUploadExternalDeviceDataDialog(true)
  };

  const handleCloseUploadExternalDeviceDataDialog = () => {
    setShowUploadExternalDeviceDataDialog(false);
    setExternalDevice(null)
  };

  const [postExternalDevice] = usePostExternalDeviceMutation();
  const handleAddExternalDevice = async (id: string, name: string, brandId: string, organizationId: number, assignedUserId: string | null, meta: string | null) => {
    await postExternalDevice({id, name, brandId, organizationId, assignedUserId, meta})
      .unwrap()
      .then((payload) => {
        setShowAddExternalDeviceDialog(false);
      })
      .catch((error) => {
        setErrorSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
        setShowErrorSnackbar(true)
      })
  };

  const [updateExternalDevice] = usePatchExternalDeviceMutation();
  const handleUpdateExternalDevice = async (externalDeviceId: string, name: string, brandId: string | null, organizationId: number | null, assignedUserId: string | null, meta: string | null) => {
    await updateExternalDevice({externalDeviceId, patchExternalDevice: {fields: ["Name", "BrandId", "OrganizationId", "AssignedUserId", "Meta"], name, brandId, lastValueAt: null, organizationId, assignedUserId, meta}})
      .unwrap()
      .then((payload) => {
        setShowAddExternalDeviceDialog(false);
      })
      .catch((error) => {
        setErrorSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
        setShowErrorSnackbar(true)
      })
  };

  const [deleteExternalDevice] = useDeleteExternalDeviceMutation();
  const handleDeleteExternalDevice = async (externalDeviceId: string) => {
    await deleteExternalDevice(externalDeviceId)
      .unwrap()
      .then((payload) => {
        setShowDeleteExternalDeviceDialog(false);
      })
      .catch((error) => {
        setErrorSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
        setShowErrorSnackbar(true)
      })
  };

  const handleUploadExternalDeviceData = async (deviceId: string, file: File) => {
    const externalDevice = externalDevices!!.find(ed => ed.id === deviceId)!!;
    try {
      setUploadProgress(0);

      await uploadToBlobStorage(file, externalDevice, (percent) => {
        setUploadProgress(percent);
      });

      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadProgress(null);
      setShowUploadExternalDeviceDataDialog(false);
      setSuccessSnackbarMessage(t('file_upload_completed', 'The file has been successfully uploaded. Processing in the cloud has started. It may take several minutes for the data to become available. This will depend, among other factors, on the file size.'))
      setShowSuccessSnackbar(true);
    } catch (error: any) {
      setUploadProgress(null);
      setErrorSnackbarMessage(error.data.message as string)
      setShowErrorSnackbar(true);
    }
  };

  const [getBlobSASToken] = useGetBlobSASTokenMutation();
  async function uploadToBlobStorage(
    file: File,
    externalDevice: IExternalDevice,
    onProgressCallback?: (percent: number) => void
  ): Promise<void> {
    const containerName = getDeviceBrandName(externalDevice.brandId!!).toLowerCase();

    const blobName = containerName === DeviceBrandName.Sykno.toLowerCase()
      ? `${externalDevice.id}/raw/${file.name}`
      : `${externalDevice.id}/${file.name}`;

    const response = await getBlobSASToken({ containerName, getBlobSasToken: { BlobName: blobName } }).unwrap();
    
    const blockBlobClient = new BlockBlobClient(response.data.url);

    await blockBlobClient.uploadData(file, {
      blobHTTPHeaders: {
        blobContentType: file.type
      },
      onProgress: (progress) => {
        if (onProgressCallback && progress.loadedBytes) {
          const percent = Math.round((progress.loadedBytes / file.size) * 100);
          onProgressCallback(percent);
        }
      }
    });
  }

  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>();

  const handleCloseErrorSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowErrorSnackbar(false);
    setErrorSnackbarMessage(null)
  };

  const [showSuccessSnackbar, setShowSuccessSnackbar] = React.useState(false);
  const [successSnackbarMessage, setSuccessSnackbarMessage] = useState<string | null>();

  const handleCloseSuccessSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowSuccessSnackbar(false);
    setSuccessSnackbarMessage(null)
  };

  const columns = useMemo(
    () => [
      {
        Header: t('id', 'Id'),
        accessor: 'id'
      }, 
      {
        Header: t('name', 'Name'),
        accessor: 'name',
        Cell: ({ row }: { row: Row }) => {
          const { values } = row;
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>
                <Typography>{values.name ?? '—'}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {values.id}
                </Typography>
              </Stack>
            </Stack>
          );
        }
      },
      {
        Header: t('brand', 'Brand'),
        accessor: 'brandId',
        sortType: (rowA: Row, rowB: Row) => {
          const brandIdA = (rowA.original as IExternalDevice).brandId ?? '';
          const brandIdB = (rowB.original as IExternalDevice).brandId ?? '';
          const a = getDeviceBrandName(brandIdA);
          const b = getDeviceBrandName(brandIdB);
          return a.localeCompare(b, i18n.language, { sensitivity: 'base' });
        },
        Cell: ({ row }: { row: Row }) => {
          return (
            <Typography>{getDeviceBrandName(row.values.brandId)}</Typography>
          )
        }
      },
      {
        Header: t('organization', 'Organization'),
        accessor: 'organizationId',
        sortType: (rowA: Row, rowB: Row) => {
          const orgA = organizations?.find(x => x.id === (rowA.original as IExternalDevice).organizationId);
          const orgB = organizations?.find(x => x.id === (rowB.original as IExternalDevice).organizationId);
          const nameA = String(orgA?.name ?? '');
          const nameB = String(orgB?.name ?? '');
          return nameA.localeCompare(nameB, i18n.language, { sensitivity: 'base' });
        },
        Cell: ({ row }: { row: Row }) => {
          const organization = organizations?.find(x => x.id == row.values.organizationId)

          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>
                <Typography>{organization?.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {organization?.id}
                </Typography>
              </Stack>
            </Stack>
          );
        }
      },
      {
        Header: t('assigned_user', 'Assigned user'),
        accessor: 'assignedUserId',
        sortType: (rowA: Row, rowB: Row) => {
          const userA = users?.find(x => x.id === (rowA.original as IExternalDevice).assignedUserId);
          const userB = users?.find(x => x.id === (rowB.original as IExternalDevice).assignedUserId);
          const fullNameA = `${userA?.firstName ?? ''} ${userA?.lastName ?? ''}`.trim();
          const fullNameB = `${userB?.firstName ?? ''} ${userB?.lastName ?? ''}`.trim();
          const nameCompare = fullNameA.localeCompare(fullNameB, i18n.language, { sensitivity: 'base' });
          if (nameCompare !== 0) return nameCompare;
          return String(userA?.id ?? '').localeCompare(String(userB?.id ?? ''), i18n.language, { sensitivity: 'base' });
        },
        Cell: ({ row }: { row: Row }) => {
          const assignedUser = users?.find(x => x.id == row.values.assignedUserId)

          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>
                <Typography>{assignedUser?.firstName} {assignedUser?.lastName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {assignedUser?.id}
                </Typography>
              </Stack>
            </Stack>
          );
        }
      },
      {
        Header: t('last_synced', 'Last synced'),
        accessor: 'lastSyncedAt',
        sortType: (rowA: Row, rowB: Row) => {
          const a = (rowA.original as IExternalDevice).lastSyncedAt;
          const b = (rowB.original as IExternalDevice).lastSyncedAt;
          return (b ? new Date(b).getTime() : 0) - (a ? new Date(a).getTime() : 0);
        },
        Cell: ({ row }: { row: Row }) => {
          const item = row.original as IExternalDevice;
          let statusChip: JSX.Element | null = null;

          switch (item.status) {
            case 'revoked':
              statusChip = <Chip color="error" label="Revoked" size="small" variant="light" />;
              break;
            case 'expired':
              statusChip = <Chip color="warning" label="Expired" size="small" variant="light" />;
              break;
            case 'active':
              statusChip = <Chip color="success" label="Active" size="small" variant="light" />;
              break;
            default:
              statusChip = item.status ? <Chip color="info" label={item.status} size="small" variant="light" /> : null;
          }

          return (
            <Stack spacing={1} alignItems="flex-start">
              <Typography>
                {item.lastSyncedAt ? moment(item.lastSyncedAt.toString()).locale(i18n.language).fromNow() : ''}
              </Typography>
              {statusChip}
            </Stack>
          );
        }
      },
      {
        Header: t('last_value', 'Last value'),
        accessor: 'lastValueAt',
        sortType: (rowA: Row, rowB: Row) => {
          const a = (rowA.original as IExternalDevice).lastValueAt;
          const b = (rowB.original as IExternalDevice).lastValueAt;
          return (b ? new Date(b).getTime() : 0) - (a ? new Date(a).getTime() : 0);
        },
        Cell: ({ row }: { row: Row }) => {
          const item = row.original as IExternalDevice;
          return (
            <Typography>{item.lastValueAt ? moment(item.lastValueAt.toString()).locale(i18n.language).fromNow() : ''}</Typography>
          );
        }
      },
      {
        Header: t('actions', 'Actions'),
        className: 'cell-center',
        disableSortBy: true,
        Cell: ({ row }: { row: Row<{}> }) => {
          const item = row.original as IExternalDevice;
          if (item.isConnection) {
            return (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
                <Tooltip title={t('disconnect', 'Disconnect')}>
                  <IconButton
                    color="warning"
                    disabled={item.status === 'revoked'}
                    onClick={() => handleOpenDeleteExternalDeviceDialog(item)}
                  >
                    <DisconnectOutlined style={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          }
          // Device actions
          const device = item as IExternalDevice;
          return (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
              <Tooltip title={t('edit', 'Edit')}>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenUpdateExternalDeviceDialog(device)}
                >
                  <EditTwoTone twoToneColor={theme.palette.primary.main} />
                </IconButton>
              </Tooltip>
              {(device.brandId === DeviceBrandId.Sykno || device.brandId === DeviceBrandId.SakuraTech) && (
                <Tooltip title={t('upload_file', 'Upload file')}>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenUploadExternalDeviceDataDialog(device)}
                  >
                    <UploadOutlined twoToneColor={theme.palette.primary.main} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t('delete', 'Delete')}>
                <IconButton
                  color="error"
                  onClick={() => handleOpenDeleteExternalDeviceDialog(device)}
                >
                  <DeleteTwoTone twoToneColor={theme.palette.error.main} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, users, organizations, t]
  );

  const selectedExternalDeviceId: string = (externalDevice?.id ?? '') as string;

  return (
    <MainCard content={false}>
      <ScrollX>
        <DevicesTable
          columns={columns}
          externalDevices={externalDevices ?? []}
          handleOpenAddExternalDeviceDialog={handleOpenAddExternalDeviceDialog}
          getHeaderProps={(column: HeaderGroup) => column.getSortByToggleProps()}
          t={t}
        isLoading={isLoadingExternalDevices || isLoadingUsers || isLoadingOrganizations}
        isFetching={isFetchingExternalDevices || isFetchingUsers || isFetchingOrganizations}
        />
      </ScrollX>
      {
        showDeleteExternalDeviceDialog &&
          <GenericDeleteDialog
            openDialog={showDeleteExternalDeviceDialog}
            handleClose={handleCloseDeleteExternalDeviceDialog}
            title={deleteDialogTitle}
            message={t('this_action_cannot_be_undone', 'This action cannot be undone.')}
            handleDelete={() => handleDeleteExternalDevice(selectedExternalDeviceId)}
          />
      }
      
      {showAddExternalDeviceDialog && <AddExternalDeviceDialog organizations={organizations!!} users={users!!} openDialog={showAddExternalDeviceDialog} handleClose={handleCloseAddExternalDeviceDialog} handleAddExternalDevice={handleAddExternalDevice} />}
      
      {showUpdateExternalDeviceDialog && externalDevice && <UpdateExternalDeviceDialog openDialog={showUpdateExternalDeviceDialog} organizations={organizations!!} users={users!!} handleClose={handleCloseUpdateExternalDeviceDialog} handleUpdateExternalDevice={handleUpdateExternalDevice} externalDevice={externalDevice} />}
    
      {showUploadExternalDeviceDataDialog && externalDevice && <UploadExternalDeviceDataDialog openDialog={showUploadExternalDeviceDataDialog} externalDevice={externalDevice} handleClose={handleCloseUploadExternalDeviceDataDialog} handleUploadExternalDeviceData={handleUploadExternalDeviceData} uploadProgress={uploadProgress}/>}

      <Snackbar open={showErrorSnackbar} autoHideDuration={6000} onClose={handleCloseErrorSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center'}}>
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>{errorSnackbarMessage}</Alert>
      </Snackbar>

      <Snackbar open={showSuccessSnackbar} autoHideDuration={6000} onClose={handleCloseSuccessSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center'}}>
        <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%' }}>{successSnackbarMessage}</Alert>
      </Snackbar>
    </MainCard>
  )
}

// ==============================|| REACT TABLE ||============================== //

interface Props {
    columns: Column[]
    externalDevices: any[]
    handleOpenAddExternalDeviceDialog: () => void
    getHeaderProps: (column: HeaderGroup) => {}
    t: TFunction<"translation", undefined, "translation">
    isLoading: boolean
    isFetching: boolean
}

function DevicesTable({ columns, externalDevices, handleOpenAddExternalDeviceDialog, getHeaderProps, t, isLoading, isFetching }: Props) {
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

    const filterTypes = useMemo(() => renderFilterTypes, []);
    const sortTypes = useMemo(() => ({
      alphanumeric: (rowA: Row, rowB: Row, columnId: string) => {
        const a = String(rowA.values[columnId] ?? '');
        const b = String(rowB.values[columnId] ?? '');
        return a.localeCompare(b, i18n.language, { sensitivity: 'base' });
      }
    }), []);
    const sortBy = { id: 'name', desc: false };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        page,
        gotoPage,
        setPageSize,
        state: { globalFilter, pageIndex, pageSize },
        preGlobalFilteredRows,
        setGlobalFilter
    } = useTable(
        {
        columns,
        data: externalDevices,
        filterTypes,
        sortTypes,
        disableSortRemove: true,
        initialState: { pageIndex: 0, pageSize: 10, hiddenColumns: ['id'], sortBy: [sortBy] }
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );

    return (
      <Stack spacing={3}>
        <Stack
          direction={matchDownSM ? 'column' : 'row'}
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 3, pb: 0 }}
          >
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            size="small"
          />
          <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={1}>
            <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleOpenAddExternalDeviceDialog} size="small" sx={{textTransform: 'none'}}>
              {t('add_device', 'Add device')}
            </Button>
          </Stack>
        </Stack>
        <Table {...getTableProps()}>
          <TableHead>
            {
              headerGroups.map((headerGroup: HeaderGroup<{}>) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {
                    headerGroup.headers.map((column: HeaderGroup) => (
                      <TableCell {...column.getHeaderProps([{ className: column.className }, getHeaderProps(column)])}>
                          <HeaderSort column={column} />
                      </TableCell>
                    ))
                  }
                </TableRow>
              ))
            }
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {
              isLoading ? (
                <EmptyTable msg={t('loading', 'Loading...')} colSpan={7} />
              ) : isFetching ? (
                <EmptyTable msg={t('fetching', 'Fetching...')} colSpan={7} />
              ) : page.length > 0 ? (
                page.map((row: Row, i: number) => {
                  prepareRow(row);

                  return (
                      <Fragment key={i}>
                      <TableRow
                          {...row.getRowProps()}
                          sx={{ cursor: 'pointer', bgcolor: 'inherit' }}
                      >
                          {row.cells.map((cell: Cell) => (
                          <TableCell {...cell.getCellProps([{ className: cell.column.className }])}>{cell.render('Cell')}</TableCell>
                          ))}
                      </TableRow>
                      {row.isExpanded}
                      </Fragment>
                  )
                })
              ) : (
                <EmptyTable msg={t('no_data_found', 'Data not found')} colSpan={7} />
              )
            }
            <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
            <TableCell sx={{ p: 2, py: 3 }} colSpan={9}>
                <TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageSize={pageSize} pageIndex={pageIndex} />
            </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>
    );
}